import { useMemo } from 'react';
import { ASK_USER_APPROVAL_KIND } from '../../../contracts/ask-user-details.types';
import type { ChatItem } from '../../../contracts/harness.types';
import type { PendingToolApproval } from '../../../contracts/tool-approval.types';
import { usePendingApprovalsForRun } from '../../inbox/gate';

interface UseProjectPendingApprovalsArgs {
  agentRunId?: string;
  items: ChatItem[];
  pendingApprovalToolCallIds: ReadonlySet<string>;
}

export const useProjectPendingApprovals = ({
  agentRunId,
  items,
  pendingApprovalToolCallIds,
}: UseProjectPendingApprovalsArgs): {
  activeClarificationApproval: PendingToolApproval | null;
  hasPendingApproval: boolean;
  /** Every approval the run is waiting on — a steer must resolve all of them. */
  pendingApprovals: PendingToolApproval[];
} => {
  const pendingHarnessApprovals = usePendingApprovalsForRun(agentRunId);

  const activeClarificationApproval = useMemo(() => {
    if (!agentRunId) return null;
    for (const approval of pendingHarnessApprovals) {
      if (approval.approvalKind === ASK_USER_APPROVAL_KIND) return approval;
    }
    return null;
  }, [agentRunId, pendingHarnessApprovals]);

  const hasPendingToolUse = useMemo(
    () =>
      items.some(
        (item) => item.kind === 'tool_use' && item.status === 'pending_approval'
      ),
    [items]
  );

  return {
    activeClarificationApproval,
    hasPendingApproval:
      pendingHarnessApprovals.length > 0 ||
      pendingApprovalToolCallIds.size > 0 ||
      hasPendingToolUse,
    pendingApprovals: pendingHarnessApprovals,
  };
};
